// src/Components/BusForm.js
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import api from '../api/api.js';
import { busSchema } from '../lib/validations';
import Button from './ui/Button.jsx';
import { useToast } from './ui/Toast.jsx';
import f from '../styles/forms.module.css';

const BusForm = ({ form, resetForm, driverOptions, busTypeOptions, onSuccess }) => {
  const isEditing = !!form.id;
  const { showToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(busSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      plate:       form.plate       || '',
      bus_type_id: form.bus_type_id ? String(form.bus_type_id) : '',
      driver_id:   form.driver_id   ? String(form.driver_id)   : '',
      status:      form.status      || 'available',
    },
  });

  useEffect(() => {
    reset({
      plate:       form.plate       || '',
      bus_type_id: form.bus_type_id ? String(form.bus_type_id) : '',
      driver_id:   form.driver_id   ? String(form.driver_id)   : '',
      status:      form.status      || 'available',
    });
  }, [form.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (data) => {
    try {
      const payload = {
        Plate:       data.plate.trim().toUpperCase(),
        ID_type_bus: Number(data.bus_type_id),
        ID_driver:   data.driver_id ? Number(data.driver_id) : null,
        Status:      data.status || 'available',
      };

      if (isEditing) {
        await api.put(`/bus/${form.id}`, payload);
        onSuccess({ ...form, ...payload, id: form.id });
      } else {
        const res = await api.post('/bus', payload);
        const busId = res.data.busId || res.data.ID;
        if (!busId) throw new Error('El servidor no devolvió el ID del bus creado');
        onSuccess({ id: busId, ...payload });
      }

      showToast(`Bus ${isEditing ? 'actualizado' : 'creado'} correctamente`, { type: 'success' });
      resetForm();
    } catch (err) {
      showToast(
        err.response?.data?.errors?.[0]?.message ||
        err.response?.data?.error ||
        err.message ||
        'Hubo un error al guardar el bus.',
        { type: 'error' }
      );
    }
  };

  const blockInvalidPlate = (e) => {
    if (e.key.length === 1 && !/[A-Za-z0-9-]/.test(e.key)) e.preventDefault();
  };

  return (
    <div className={f.card}>
      {/* Header */}
      <div className={f.cardHeader}>
        <div className={f.cardTitleGroup}>
          <h3 className={f.cardTitle}>{isEditing ? 'Editar Bus' : 'Registrar Bus'}</h3>
          <p className={f.cardDesc}>
            {isEditing
              ? 'Modifica los datos del vehículo seleccionado.'
              : 'Completa los datos para registrar un nuevo vehículo en la flota.'}
          </p>
        </div>
        <span className={f.badgeAdmin}>Admin</span>
      </div>
      <hr className={f.divider} />

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* ── Sección: Identificación ── */}
        <div className={f.section}>
          <span className={f.sectionLabel}>Identificación del Vehículo</span>
          <div className={f.grid2}>
            {/* Placa */}
            <div className={f.field}>
              <label
                htmlFor="bus-plate"
                className={`${f.label} ${f.required}`}
              >
                Placa
              </label>
              <input
                id="bus-plate"
                placeholder="Ej: ABC-123"
                className={`${f.input} ${errors.plate ? f.inputError : ''}`}
                aria-describedby={errors.plate ? 'plate-error' : 'plate-hint'}
                aria-invalid={!!errors.plate}
                onKeyDown={blockInvalidPlate}
                style={{ textTransform: 'uppercase' }}
                {...register('plate')}
              />
              {errors.plate
                ? <span id="plate-error" className={f.fieldError} role="alert">{errors.plate.message}</span>
                : <span id="plate-hint" className={f.fieldHint}>Solo letras, números y guiones. Ej: ABC-123</span>
              }
            </div>

            {/* Tipo de Bus */}
            <div className={f.field}>
              <label
                htmlFor="bus-type"
                className={`${f.label} ${f.required}`}
              >
                Tipo de Bus
              </label>
              <select
                id="bus-type"
                className={`${f.select} ${errors.bus_type_id ? f.inputError : ''}`}
                aria-describedby={errors.bus_type_id ? 'bus-type-error' : undefined}
                aria-invalid={!!errors.bus_type_id}
                {...register('bus_type_id')}
              >
                <option value="">Seleccione un tipo</option>
                {busTypeOptions.map(opt => (
                  <option key={opt.value} value={String(opt.value)}>{opt.label}</option>
                ))}
              </select>
              {errors.bus_type_id && (
                <span id="bus-type-error" className={f.fieldError} role="alert">
                  {errors.bus_type_id.message}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Sección: Asignación ── */}
        <div className={f.section}>
          <span className={f.sectionLabel}>Asignación y Estado</span>
          <div className={f.grid2}>
            {/* Conductor */}
            <div className={f.field}>
              <label htmlFor="bus-driver" className={f.label}>Conductor Asignado</label>
              <select
                id="bus-driver"
                className={f.select}
                {...register('driver_id')}
              >
                <option value="">Sin conductor asignado</option>
                {driverOptions.map(opt => (
                  <option key={opt.value} value={String(opt.value)}>{opt.label}</option>
                ))}
              </select>
              <span className={f.fieldHint}>Opcional — puede asignarse después</span>
            </div>

            {/* Estado — solo en edición */}
            {isEditing && (
              <div className={f.field}>
                <label htmlFor="bus-status" className={`${f.label} ${f.required}`}>Estado</label>
                <select id="bus-status" className={f.select} {...register('status')}>
                  <option value="available">Disponible</option>
                  <option value="maintenance">En Mantenimiento</option>
                  <option value="outservice">Fuera de Servicio</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* ── Acciones ── */}
        <div className={f.actions}>
          <Button type="submit" variant="primary" loading={isSubmitting}>
            {isEditing ? 'Actualizar Bus' : 'Registrar Bus'}
          </Button>
          {isEditing && (
            <Button type="button" variant="secondary" onClick={resetForm}>
              Cancelar
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default BusForm;
